/**
 * Created by rastwe on 1/13/2017.
 * @module $Name$
 * @extends React.Component
 */
/* eslint react/no-multi-comp: 0*/
import './app.less';
import './globals.less';
import React, { useEffect, useState, Fragment, useMemo, useLayoutEffect } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { hot } from 'react-hot-loader';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import Async from '@ey/async';
import { getTheme } from '@ey/styled-theme';
import Errors from '../common/components/Errors/Errors';
import LoaderContainer from '../common/components/LoaderContainer/LoaderContainer';
import Main from '../common/components/Main/Main';
import Nav from '../common/components/Nav/Nav';
import { getUserById } from '../features/users/users';
import {
	Theme,
	routesResources,
	loadingAreas
} from '../common/utils/uiconstants';
import { raiseJSValidationError } from './../common/errors';
import { getUserById as getAdminUserById } from '../features/admin/admin';
import { useLocation } from 'react-router-dom';
import { useDispatchAction } from '../common/utils/customHooks';
import { selectArea } from '../common/selectors/loading-selector';
import {
	adobeAnalytics
} from '@ey/voltron-analytics';
import envConfig from '@ey/voltron-config';
import {processPathname, processHashPath, assignPageName} from '../common/components/AdobeAnalytics/helper';

const adobeAnalyticsEnabled = true;
let adobeAnalyticsEmbedded = false;

/* the import() call must be located in this outer function. Placing it
 * inside load() will cause Webpack to raise a warning about a required
 * file path being an expression.
 * The value of the component={} attribute must be a function or a
 * React component. React-Router sends the props in as the first parameter,
 * so grab those and forward them to the Async.load() as the first
 * argument. The second argument must be an import() statement. */

const DryDock = (props) =>
	Async.load(props, import('../common/components/DryDock/DryDock'));

const UserSettings = (props) =>
	Async.load(
		props,
		import('../features/users/components/UserSettings/UserSettings')
	);

const Projects = (props) =>
	Async.load(props, import('../features/projects/components/Project/Project'));

const Project = (props) =>
	Async.load(
		props,
		import('../features/projects/components/ProjectDetail/ProjectDetail')
	);

const ProjectSummary = (props) =>
	Async.load(
		props,
		import(
			'../features/projectSummary/components/ProjectSummary/projectSummary'
		)
	);

const AdminPortal = (props) =>
	Async.load(props, import('../features/admin/components/Admin/Admin'));

const routes = [
	{
		path: '/dryDock',
		component: DryDock
	},
	{
		path: '/settings',
		component: UserSettings
	},
	{
		path: '/projects',
		component: Projects
	},
	{
		path: '/project/:projectId?',
		component: Project
	},
	{
		path: '/projectsummary/:projectId?/:activeTab?',
		component: ProjectSummary
	},
	{
		path: '/',
		component: Projects
	}	
];

const adminRoute = {
	path: `/${routesResources.admin}/:cardId?/:partitionKey?`
};

const App = () => {
	const { pathname, hash } = useLocation();

	const dispatch = useDispatchAction([loadingAreas.app]);

	const isLoading = useSelector(selectArea(loadingAreas.app));

	const isAdminPageRequest = useMemo(() => {
		return pathname.toLowerCase().includes(`/${routesResources.admin}`);
	}, [pathname]);

	const user = useSelector((store) =>
		isAdminPageRequest ? store.admin.user : store.user
	);

	const [redirectToSettings, setRedirectToSettings] = useState(false);
	const [hasMounted, setHasMounted] = useState(false);

	const setUserTheme = () => {
		const body = document.getElementsByTagName('body');
		body[0].setAttribute('motif-theme', user?.theme ?? Theme.Light);
	};

	// Adobe Analytics
	const [prevRoute, setPrevRoute] = useState(pathname + hash);
	const pageTrack = (includeRoot) => {
		if (pathname === '/' && !includeRoot) return;
		const processedPathname = processPathname(pathname);
		const hashPath = processHashPath(hash, processedPathname.child);
		const overrides = {
			page: {
				hierarchy1: processedPathname.parent,
				hierarchy2: hashPath,
				pageName: assignPageName(processedPathname.parent, processedPathname.child, hashPath)
			}
		};

		if (adobeAnalyticsEnabled && user?.emailAddress) {
			adobeAnalytics.embed(
				envConfig.adobeAnalytics.propertyId,
				adobeAnalytics.dataOverrides(envConfig.adobeAnalytics.digitalData, overrides),
				user.emailAddress.toLowerCase()
			);
		}
	};

	// Page Load - Tracking
	useEffect(() => {
		if (user?.emailAddress) pageTrack(true);
	}, [user]);

	// Page Change - Tracking
	useEffect(() => {
		if (prevRoute != pathname + hash) {
			pageTrack(false);
			setPrevRoute(pathname + hash);
		}
	}, [pathname, hash]);

	useEffect(() => {
		setUserTheme();

		setHasMounted(true);

		if (!isAdminPageRequest) {
			const promise = dispatch(getUserById());

			promise.then(({ response }) => {
				if (response?.hasOwnProperty('isUserPreferenceRequested')) {
					setRedirectToSettings(response.isUserPreferenceRequested);
				}
			});

			promise.catch((error) => {
				if (error.response && error.response.data) {
					if (error.response.data.errors?.Error[0]?.substr(0, 4) === '1001') {
						dispatch(raiseJSValidationError('default'), true);
					}
				}
			});
		} else {
			dispatch(getAdminUserById());
		}
	}, []);

	useEffect(() => {
		if (user?.isUserPreferenceRequested !== undefined) {
			setRedirectToSettings(user.isUserPreferenceRequested);
			setUserTheme();
		}
	}, [user]);

	// Generate redirect routes when user needs to complete settings
	const getRedirectRoutes = () => {
		if (!isAdminPageRequest && redirectToSettings) {
			return [
				<Route 
					key="redirect-root"
					exact 
					path="/" 
					render={() => <Redirect to="/settings" />} 
				/>
			];
		}
		return [];
	};

	let isDark = false;
	if (user?.theme && user.theme.toLowerCase() === Theme.Dark) {
		isDark = true;
	}

	const adminJsx = (
		<Fragment>
			<Route exact={false} key={adminRoute.path} path={adminRoute.path}>
				<Nav />
				<Main>
					<LoaderContainer isLoading={isLoading}>
						{hasMounted && <AdminPortal />}
					</LoaderContainer>
				</Main>
			</Route>
		</Fragment>
	);

	const economicsJsx = (
		<Fragment>
			<Nav />
			<Main>
				<LoaderContainer isLoading={isLoading}>
					<Switch>
						{getRedirectRoutes()}
						{routes.map(({ path, isExact, component }) => {
							return (
								<Route
									exact={isExact}
									key={path}
									path={path}
									render={component}
								/>
							);
						})}
					</Switch>
				</LoaderContainer>
			</Main>
		</Fragment>
	);

	return (
		<ThemeProvider theme={getTheme(isDark)}>
			<section className="root">
				<Errors />
				{!isAdminPageRequest ? economicsJsx : adminJsx}
			</section>
		</ThemeProvider>
	);
};

export default hot(module)(App);
